// ✅ src/pages/LoginPage.js (FULL - POPUP ONLY, NO REDIRECT)
// ✅ Uses Google POPUP everywhere (no signInWithRedirect)
// ✅ Keeps your UI + referral + consent checkbox
// ✅ ✅ REMOVED: email verification gate + verification UI

import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../lib/firebase";
import {
  loginWithGoogle,
  loginWithEmail,
  signUpWithEmail,
  resetPassword,
} from "../lib/auth";
import {
  addDoc,
  collection,
  getDocs,
  limit,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}

export default function LoginPage({ onSuccess, goBack }) {
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyGoogle, setBusyGoogle] = useState(false);

  // ✅ required for signup (Google certification)
  const [agree, setAgree] = useState(false);

  // ✅ referral code from URL / localStorage
  const [refCode, setRefCode] = useState("");

  // ✅ show any message passed from guards
  useEffect(() => {
    const m = safe(location.state?.msg);
    if (m) setMsg(m);
  }, [location.state]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const ref = safe(sp.get("ref"));

    if (ref) {
      setRefCode(ref);
      try {
        localStorage.setItem("pendingReferralCode", ref);
      } catch {}
    } else {
      try {
        const saved = safe(localStorage.getItem("pendingReferralCode"));
        if (saved) setRefCode(saved);
      } catch {}
    }
  }, [location.search]);

  const tryCreateReferral = async () => {
    try {
      const code =
        safe(refCode) || safe(localStorage.getItem("pendingReferralCode"));
      if (!code) return;

      const u = auth.currentUser;
      if (!u?.uid) return;

      const newUid = u.uid;

      const refUserQ = query(
        collection(db, "users"),
        where("referralCode", "==", code),
        limit(1)
      );
      const refUserSnap = await getDocs(refUserQ);
      if (refUserSnap.empty) return;

      const referrerUid = refUserSnap.docs[0].id;
      if (referrerUid === newUid) return;

      const dupQ = query(
        collection(db, "referrals"),
        where("referrerUid", "==", referrerUid),
        where("referredUid", "==", newUid),
        limit(1)
      );
      const dupSnap = await getDocs(dupQ);
      if (!dupSnap.empty) {
        try {
          localStorage.removeItem("pendingReferralCode");
        } catch {}
        setRefCode("");
        return;
      }

      await addDoc(collection(db, "referrals"), {
        referrerUid,
        referredUid: newUid,
        rewardAmount: 7,
        currency: "GHS",
        status: "PENDING",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        qualifiedAt: null,
        rewardedAt: null,
      });

      try {
        localStorage.removeItem("pendingReferralCode");
      } catch {}
      setRefCode("");
    } catch (e) {
      console.error("[LOGIN][REF] create referral failed:", e);
    }
  };

  const styles = useMemo(
    () => ({
      page: {
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 18,
        position: "relative",
        overflow: "hidden",
        background:
          "radial-gradient(900px 600px at 20% 20%, rgba(99,102,241,0.20), transparent 60%), radial-gradient(900px 600px at 80% 40%, rgba(236,72,153,0.16), transparent 60%), linear-gradient(180deg, #0b1020 0%, #090b13 100%)",
      },

      blob: (x, y, s, o) => ({
        position: "absolute",
        width: s,
        height: s,
        left: x,
        top: y,
        borderRadius: "999px",
        background:
          "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.18), rgba(255,255,255,0.02) 55%, transparent 70%)",
        filter: "blur(2px)",
        opacity: o,
        zIndex: 0,
      }),

      shell: { width: "100%", maxWidth: 520, zIndex: 1 },

      card: {
        borderRadius: 24,
        background: "#ffffff",
        boxShadow: "0 16px 60px rgba(0,0,0,0.45)",
        padding: 26,
        minHeight: 560,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      },

      formTitle: {
        fontSize: 22,
        fontWeight: 900,
        letterSpacing: -0.2,
        marginBottom: 6,
        color: "#0b1020",
      },
      formSub: {
        fontSize: 13,
        color: "rgba(11,16,32,0.65)",
        marginBottom: 16,
        lineHeight: 1.5,
      },

      refPill: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        borderRadius: 999,
        fontSize: 12,
        color: "#0b1020",
        background: "rgba(99,102,241,0.10)",
        border: "1px solid rgba(99,102,241,0.25)",
        marginBottom: 14,
        width: "fit-content",
      },
      refTag: {
        padding: "3px 8px",
        borderRadius: 999,
        fontWeight: 800,
        background: "rgba(236,72,153,0.12)",
        border: "1px solid rgba(236,72,153,0.25)",
      },

      googleBtn: {
        width: "100%",
        padding: "14px 14px",
        borderRadius: 14,
        border: "1px solid rgba(11,16,32,0.10)",
        background: "#fff",
        color: "#0b1020",
        fontWeight: 800,
        cursor: busyGoogle || busy ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        boxShadow: "0 10px 24px rgba(11,16,32,0.08)",
      },
      gIcon: {
        width: 18,
        height: 18,
        borderRadius: 6,
        background:
          "conic-gradient(from 0deg, #4285F4, #34A853, #FBBC05, #EA4335, #4285F4)",
      },

      divider: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        margin: "14px 0",
      },
      line: { flex: 1, height: 1, background: "rgba(11,16,32,0.10)" },
      or: { fontSize: 12, color: "rgba(11,16,32,0.55)", fontWeight: 700 },

      field: { display: "grid", gap: 6, marginBottom: 12 },
      label: { fontSize: 12, fontWeight: 800, color: "rgba(11,16,32,0.75)" },

      input: {
        width: "100%",
        padding: 14,
        borderRadius: 14,
        border: "1px solid rgba(11,16,32,0.12)",
        background: "#fff",
        color: "#0b1020",
        outline: "none",
        fontSize: 16,
        WebkitTextSizeAdjust: "100%",
        boxShadow: "0 8px 16px rgba(11,16,32,0.04)",
      },

      row: { display: "flex", justifyContent: "space-between", marginTop: 6 },

      linkBtn: {
        border: "none",
        background: "transparent",
        color: "rgba(11,16,32,0.75)",
        cursor: busy ? "not-allowed" : "pointer",
        fontSize: 13,
        textDecoration: "underline",
        padding: 0,
        fontWeight: 700,
      },

      btnRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },

      primaryBtn: {
        padding: 14,
        borderRadius: 14,
        border: "none",
        background:
          "linear-gradient(135deg, rgba(99,102,241,1), rgba(236,72,153,1))",
        color: "#fff",
        fontWeight: 900,
        cursor: busy ? "not-allowed" : "pointer",
        boxShadow: "0 14px 30px rgba(99,102,241,0.25)",
      },

      ghostBtn: {
        padding: 14,
        borderRadius: 14,
        border: "1px solid rgba(11,16,32,0.12)",
        background: "#fff",
        color: "#0b1020",
        fontWeight: 900,
        cursor: busy ? "not-allowed" : "pointer",
      },

      consentRow: {
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        marginTop: 8,
        padding: 12,
        borderRadius: 14,
        border: "1px solid rgba(11,16,32,0.12)",
        background: "rgba(11,16,32,0.02)",
      },
      checkbox: { width: 18, height: 18, marginTop: 2 },
      consentText: {
        fontSize: 12,
        color: "rgba(11,16,32,0.70)",
        lineHeight: 1.45,
      },

      legal: {
        marginTop: 12,
        fontSize: 12,
        color: "rgba(11,16,32,0.6)",
        lineHeight: 1.45,
      },
      legalLink: { color: "#0b1020", textDecoration: "underline" },

      backBtn: {
        marginTop: 14,
        padding: 12,
        borderRadius: 14,
        border: "1px solid rgba(11,16,32,0.12)",
        background: "rgba(11,16,32,0.03)",
        color: "#0b1020",
        cursor: "pointer",
        fontWeight: 900,
      },

      alert: {
        marginTop: 14,
        padding: 12,
        borderRadius: 14,
        background: "rgba(255, 60, 120, 0.08)",
        border: "1px solid rgba(255, 60, 120, 0.22)",
        fontSize: 13,
        color: "rgba(160, 20, 65, 0.95)",
        lineHeight: 1.4,
      },

      success: {
        marginTop: 14,
        padding: 12,
        borderRadius: 14,
        background: "rgba(34,197,94,0.10)",
        border: "1px solid rgba(34,197,94,0.25)",
        fontSize: 13,
        color: "rgba(20,83,45,0.95)",
        lineHeight: 1.4,
      },
    }),
    [busy, busyGoogle]
  );

  // ✅ NO verification gate anymore
  const finishAuth = async () => {
    await tryCreateReferral();

    const u = auth.currentUser;
    if (!u) throw new Error("No user");

    await onSuccess();
  };

  // ✅ finish login when Firebase auth state becomes available
  useEffect(() => {
    let running = false;

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) return;
      if (running) return;
      running = true;

      try {
        setMsg("");
        // Only show Google spinner if we're in a Google flow (harmless)
        setBusyGoogle((prev) => prev);

        await finishAuth();
      } catch (e) {
        setMsg(e?.message || "Login finish failed");
      } finally {
        running = false;
      }
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSubmit = safe(email) && safe(password);

  return (
    <div style={styles.page}>
      <div style={styles.blob("6%", "10%", 260, 0.7)} />
      <div style={styles.blob("76%", "18%", 320, 0.55)} />
      <div style={styles.blob("20%", "76%", 300, 0.4)} />

      <div style={styles.shell}>
        <div style={styles.card}>
          <div style={styles.formTitle}>Welcome back</div>
          <div style={styles.formSub}>
            Log in, or create an account in seconds.
          </div>

          {refCode ? (
            <div style={styles.refPill}>
              Referral detected <span style={styles.refTag}>{refCode}</span>
              <span style={{ opacity: 0.7 }}>(linked after login)</span>
            </div>
          ) : null}

          <button
            style={styles.googleBtn}
            disabled={busy || busyGoogle}
            onClick={async () => {
              try {
                setMsg("");
                setBusyGoogle(true);

                const res = await loginWithGoogle();
                if (!res?.user) throw new Error("Google login failed");

                await finishAuth();
              } catch (e) {
                setMsg(e?.message || "Google login failed");
              } finally {
                setBusyGoogle(false);
              }
            }}
          >
            <span style={styles.gIcon} />
            {busyGoogle ? "Please wait..." : "Continue with Google"}
          </button>

          <div style={styles.divider}>
            <div style={styles.line} />
            <div style={styles.or}>OR</div>
            <div style={styles.line} />
          </div>

          <div style={styles.field}>
            <div style={styles.label}>Email</div>
            <input
              style={styles.input}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              inputMode="email"
            />
          </div>

          <div style={styles.field}>
            <div style={styles.label}>Password</div>
            <input
              style={styles.input}
              placeholder="••••••••"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <div style={styles.row}>
              <span />
              <button
                type="button"
                style={styles.linkBtn}
                disabled={busy}
                onClick={async () => {
                  if (!safe(email)) {
                    setMsg("Enter your email first, then tap “Forgot password?”");
                    return;
                  }
                  try {
                    setMsg("");
                    setBusy(true);
                    await resetPassword(safe(email));
                    setMsg("Password reset email sent. Check your inbox (and spam).");
                  } catch (e) {
                    setMsg(e?.message || "Failed to send reset email");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Forgot password?
              </button>
            </div>
          </div>

          <div style={styles.consentRow}>
            <input
              style={styles.checkbox}
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              disabled={busy}
            />
            <div style={styles.consentText}>
              I confirm I am <b>18+</b> and I agree to the{" "}
              <Link to="/terms" style={styles.legalLink}>
                Terms
              </Link>
              ,{" "}
              <Link to="/privacy" style={styles.legalLink}>
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link to="/guidelines" style={styles.legalLink}>
                Community Guidelines
              </Link>
              .
            </div>
          </div>

          <div style={styles.btnRow}>
            <button
              style={styles.primaryBtn}
              disabled={busy || !canSubmit}
              onClick={async () => {
                try {
                  setMsg("");
                  setBusy(true);

                  await loginWithEmail(email, password);
                  await finishAuth();
                } catch (e) {
                  setMsg(e?.message || "Login failed");
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Please wait..." : "Login"}
            </button>

            <button
              style={styles.ghostBtn}
              disabled={busy || !canSubmit}
              onClick={async () => {
                try {
                  if (!agree) {
                    setMsg(
                      "Please confirm you are 18+ and agree to Terms, Privacy, and Guidelines to create an account."
                    );
                    return;
                  }

                  setMsg("");
                  setBusy(true);

                  await signUpWithEmail(email, password);
                  await finishAuth();
                } catch (e) {
                  setMsg(e?.message || "Signup failed");
                } finally {
                  setBusy(false);
                }
              }}
            >
              Create account
            </button>
          </div>

          <div style={styles.legal}>
            By continuing, you agree to our{" "}
            <Link to="/terms" style={styles.legalLink}>
              Terms
            </Link>
            ,{" "}
            <Link to="/privacy" style={styles.legalLink}>
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link to="/guidelines" style={styles.legalLink}>
              Community Guidelines
            </Link>
            .
          </div>

          <button style={styles.backBtn} onClick={goBack}>
            ← Back
          </button>

          {msg ? (
            <div
              style={
                msg.toLowerCase().includes("sent") ||
                msg.toLowerCase().includes("opening") ||
                msg.toLowerCase().includes("resent") ||
                msg.toLowerCase().includes("please")
                  ? styles.success
                  : styles.alert
              }
            >
              {msg}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}